// src/modals/OpportunityTeamModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import CallIcon from '@mui/icons-material/Call';
import { alpha } from '@mui/material/styles';
import { Logger } from '../utils/logger';

/**
 * OpportunityTeamModal displays a detailed view of all team members assigned to an opportunity
 */
const logger = new Logger('OpportunityTeamModal');

const OpportunityTeamModal = ({
  open,
  onClose,
  opportunityName,
  opportunityId,
  clientName,
  team = [],
  isLoading = false
}) => {
  // Communication handler functions
  const handleEmail = (email, name) => {
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      logger.debug(`No email available for ${name}`);
    }
  };
  
  const handleChat = (employeeId, name) => {
    if (employeeId) {
      // Construct Teams chat URL with employee email
      const email = `${employeeId.toLowerCase()}@mbco.com`;
      window.open(`https://teams.microsoft.com/l/chat/0/0?users=${email}`, '_blank');
    } else {
      logger.debug(`Cannot chat with ${name} - no employee ID`);
    }
  };
  
  const handleCall = (phone, name) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      logger.debug(`No phone number available for ${name}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#121212',
          color: '#fff',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#1A1A1A',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box>
          <Typography variant="h6" component="div">
            Pursuit Team Members
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {opportunityName} - {clientName}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2, bgcolor: '#121212' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : team.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Contact</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {team.map((member, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ color: '#64B5F6' }}>
                        {member.name}
                      </Typography>
                      {member.employee_id && (
                        <Typography variant="caption" color="text.secondary">
                          {member.employee_id}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography>{member.role}</Typography>
                  </TableCell>
                  <TableCell>
                    {member.is_terminated ? (
                      <Chip
                        label="Terminated"
                        size="small"
                        sx={{
                          bgcolor: alpha('#CF6679', 0.15),
                          color: '#CF6679'
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={member.email ? `Email ${member.name.split(' ')[0]}` : `No email available`}>
                          <IconButton 
                            size="small" 
                            sx={{ color: member.email ? '#64B5F6' : 'rgba(100, 181, 246, 0.5)' }}
                            onClick={() => handleEmail(member.email, member.name)}
                            disabled={!member.email}
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={`Chat with ${member.name.split(' ')[0]}`}>
                          <IconButton 
                            size="small" 
                            sx={{ color: '#BB86FC' }}
                            onClick={() => handleChat(member.employee_id, member.name)}
                          >
                            <ChatIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={member.phone ? `Call ${member.name.split(' ')[0]}` : `No phone number available`}>
                          <IconButton 
                            size="small" 
                            sx={{ color: member.phone ? '#03DAC6' : 'rgba(3, 218, 198, 0.5)' }}
                            onClick={() => handleCall(member.phone, member.name)}
                            disabled={!member.phone}
                          >
                            <CallIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No team members assigned to this opportunity yet.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

OpportunityTeamModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  opportunityName: PropTypes.string,
  opportunityId: PropTypes.string,
  clientName: PropTypes.string,
  team: PropTypes.array,
  isLoading: PropTypes.bool
};

export default OpportunityTeamModal;
